import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { useEffectButNotOnMount } from '@hooks/useEffectButNotOnMount';
import { RootState } from '@state/ducks';
import {
  ProductDetailAction,
  SET_ASSETS,
  SET_VARIANT_SPECS_LOADING,
} from '@state/ducks/productdetail/productdetail.reducer';
import { useDispatch, useSelector } from 'react-redux';
import { ProductAssets } from '../ProductMediaLayout/ProductMediaLayout';

export interface ProductAssetsRequest {
  modelAgilityId: number;
  variantAgilityId?: number;
  cultureCode: string;
  published: boolean;
}

function fetchProductAssets(
  modelAgilityId: number | null,
  variantAgilityId: number | null,
  cultureCode: string | null,
  published: boolean,
): Promise<ProductAssets | null> {
  if (modelAgilityId === null) {
    return Promise.resolve(null);
  }
  if (cultureCode === null || cultureCode === '') {
    return Promise.resolve(null);
  }

  const params: ProductAssetsRequest = {
    modelAgilityId,
    variantAgilityId: variantAgilityId ?? undefined,
    cultureCode,
    published,
  };

  return ApiService.request<ProductAssets>({
    method: 'GET',
    baseUrl: UrlResolver.getDomain(),
    controller: 'product-detail',
    slug: 'product-assets',
    params,
  });
}

export const useProductAssets = (): boolean => {
  const modelAgilityId = useSelector<RootState, number | null>((state) => state.productDetail.modelAgilityId);
  const variantAgilityId = useSelector<RootState, number | null>(
    (state) => state.productDetail.selectedVariantAgilityId,
  );
  const pageContext = useSelector<RootState, PageContext>((state) => state.pageContext);
  const isLoadingAssets = useSelector<RootState, boolean>((x) => x.productDetail.loadingAssets);
  const dispatch = useDispatch();

  useEffectButNotOnMount(() => {
    dispatch<ProductDetailAction>({ type: SET_VARIANT_SPECS_LOADING, payload: true });
    fetchProductAssets(
      modelAgilityId,
      variantAgilityId,
      pageContext.documentCulture,
      pageContext.showOnlyPublishedProducts,
    ).then((assets) => {
      if (!assets) {
        return;
      }

      dispatch<ProductDetailAction>({ type: SET_ASSETS, payload: assets });
    });
  }, [variantAgilityId]);

  return isLoadingAssets;
};
